import { useQuery } from "@tanstack/react-query"
import { useState } from "react"
import { getAgentInteractions } from "../api/endpoints/agents"
import { AgentClientDrawer } from "../components/agent_client_drawer"
import { PageSection, StatusPill } from "../components/ui"
import { useAuth } from "../stores/auth"
import {
  formatDateTime,
  formatDurationSeconds,
  formatEnumLabel,
} from "../utils/formatting"

function outcomeTone(
  outcome?: string | null,
): "neutral" | "success" | "warning" | "danger" | "info" {
  switch (outcome) {
    case "BROKEN_PROMISE":
    case "REFUSED_TO_PAY":
      return "danger"
    case "PROMISE_TO_PAY":
    case "PARTIAL_PAYMENT":
      return "warning"
    case "FULL_PAYMENT":
      return "success"
    default:
      return "info"
  }
}

export function InteractionLogPage() {
  const { user } = useAuth()
  const agentId = user?.userId ?? ""
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null)
  const [search, setSearch] = useState("")
  const [interactionType, setInteractionType] = useState("")
  const [outcome, setOutcome] = useState("")
  const [createdAfter, setCreatedAfter] = useState("")
  const [createdBefore, setCreatedBefore] = useState("")
  const [statusChangeOnly, setStatusChangeOnly] = useState(false)
  const [page, setPage] = useState(1)

  const interactionsQuery = useQuery({
    queryKey: [
      "agent-interactions",
      agentId,
      search,
      interactionType,
      outcome,
      createdAfter,
      createdBefore,
      statusChangeOnly,
      page,
    ],
    queryFn: () => {
      const params: Record<string, string> = {
        page: String(page),
        per_page: "25",
      }

      if (search) params.search = search
      if (interactionType) params.interaction_type = interactionType
      if (outcome) params.outcome = outcome
      if (createdAfter) params.created_after = new Date(createdAfter).toISOString()
      if (createdBefore) params.created_before = new Date(createdBefore).toISOString()
      if (statusChangeOnly) params.status_change_only = "true"

      return getAgentInteractions(agentId, params)
    },
    enabled: Boolean(agentId),
  })

  const rows = interactionsQuery.data?.data?.rows ?? []
  const meta = interactionsQuery.data?.meta

  return (
    <div className="flex flex-col gap-5">
      <PageSection title="Interaction log">
        <div className="mb-5 grid grid-cols-1 gap-3 lg:grid-cols-3">
          <input
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(1)
            }}
            placeholder="Search client name, client ID, or loan ID"
            value={search}
          />
          <select
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            onChange={(event) => {
              setInteractionType(event.target.value)
              setPage(1)
            }}
            value={interactionType}
          >
            <option value="">All channels</option>
            <option value="PHONE_CALL">Phone Call</option>
            <option value="SMS">SMS</option>
            <option value="WHATSAPP">WhatsApp</option>
            <option value="IN_PERSON">In Person</option>
            <option value="EMAIL">Email</option>
            <option value="OTHER">Other</option>
          </select>
          <select
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            onChange={(event) => {
              setOutcome(event.target.value)
              setPage(1)
            }}
            value={outcome}
          >
            <option value="">All outcomes</option>
            <option value="REACHED">Reached</option>
            <option value="NOT_REACHED">Not Reached</option>
            <option value="WRONG_NUMBER">Wrong Number</option>
            <option value="PROMISE_TO_PAY">Promised to Pay</option>
            <option value="REFUSED_TO_PAY">Refused to Pay</option>
            <option value="DISPUTED">Disputed</option>
            <option value="ESCALATED">Escalated</option>
            <option value="CALLBACK_REQUESTED">Callback Requested</option>
            <option value="PARTIAL_PAYMENT">Partial Payment</option>
            <option value="FULL_PAYMENT">Full Payment</option>
            <option value="BROKEN_PROMISE">Broken Promise</option>
          </select>
          <input
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            onChange={(event) => {
              setCreatedAfter(event.target.value)
              setPage(1)
            }}
            type="datetime-local"
            value={createdAfter}
          />
          <input
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            onChange={(event) => {
              setCreatedBefore(event.target.value)
              setPage(1)
            }}
            type="datetime-local"
            value={createdBefore}
          />
          <label className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
            <input
              checked={statusChangeOnly}
              onChange={(event) => {
                setStatusChangeOnly(event.target.checked)
                setPage(1)
              }}
              type="checkbox"
            />
            Status changes only
          </label>
        </div>

        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50 font-bold uppercase text-slate-500">
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3">Client</th>
              <th className="px-4 py-3">Channel</th>
              <th className="px-4 py-3">Outcome</th>
              <th className="px-4 py-3">Notes</th>
              <th className="px-4 py-3">Duration</th>
              <th className="px-4 py-3">Status change</th>
              <th className="px-4 py-3">Follow-up</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row: any) => (
              <tr
                className="cursor-pointer transition-colors hover:bg-slate-50"
                key={row.interactionId}
                onClick={() => setSelectedClientId(row.clientId)}
              >
                <td className="px-4 py-3">{formatDateTime(row.createdAt)}</td>
                <td className="px-4 py-3">
                  <strong>{row.clientName}</strong>
                  <div className="text-xs text-slate-500">{row.loanId}</div>
                </td>
                <td className="px-4 py-3">{formatEnumLabel(row.interactionType)}</td>
                <td className="px-4 py-3">
                  <StatusPill tone={outcomeTone(row.outcome)}>{formatEnumLabel(row.outcome)}</StatusPill>
                </td>
                <td className="max-w-xs px-4 py-3">
                  <div className="truncate text-slate-700">{row.notes ?? "-"}</div>
                </td>
                <td className="px-4 py-3">{formatDurationSeconds(row.durationSeconds)}</td>
                <td className="px-4 py-3">
                  {row.toPipelineStatus ? (
                    <>
                      {formatEnumLabel(row.fromPipelineStatus)} {"->"}{" "}
                      {formatEnumLabel(row.toPipelineStatus)}
                    </>
                  ) : (
                    "-"
                  )}
                </td>
                <td className="px-4 py-3">{formatDateTime(row.followUpDate)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
          <span>
            Page {meta?.page ?? 1} of {meta?.totalPages ?? 1}
          </span>
          <div className="flex gap-2">
            <button
              className="rounded-md border border-slate-200 bg-white px-3 py-1.5"
              disabled={(meta?.page ?? 1) <= 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
              type="button"
            >
              Previous
            </button>
            <button
              className="rounded-md border border-slate-200 bg-white px-3 py-1.5"
              disabled={(meta?.page ?? 1) >= (meta?.totalPages ?? 1)}
              onClick={() => setPage((value) => value + 1)}
              type="button"
            >
              Next
            </button>
          </div>
        </div>
      </PageSection>

      <AgentClientDrawer
        agentId={agentId}
        clientId={selectedClientId}
        onClose={() => setSelectedClientId(null)}
      />
    </div>
  )
}
